import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { universityAPI } from '../../services/api';

// Fetch all universities with optional relationships
export const fetchUniversities = createAsyncThunk('university/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await universityAPI.getAll(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch universities');
  }
});

// Fetch single university with full details
export const fetchUniversityById = createAsyncThunk('university/fetchById', async ({ id, includeCourses = true }, { rejectWithValue }) => {
  try {
    const response = await universityAPI.getById(id, includeCourses);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch university');
  }
});

// Create university
export const createUniversity = createAsyncThunk('university/create', async (data, { rejectWithValue }) => {
  try {
    const response = await universityAPI.create(data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create university');
  }
});

// Update university
export const updateUniversity = createAsyncThunk('university/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await universityAPI.update(id, data);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update university');
  }
});

// Delete university
export const deleteUniversity = createAsyncThunk('university/delete', async (id, { rejectWithValue }) => {
  try {
    await universityAPI.delete(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete university');
  }
});

const universitySlice = createSlice({
  name: 'university',
  initialState: { 
    universities: [], 
    selectedUniversity: null,
    loading: false, 
    error: null, 
    pagination: null 
  },
  reducers: { 
    clearError: (state) => { state.error = null; },
    clearSelectedUniversity: (state) => { state.selectedUniversity = null; }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all universities
      .addCase(fetchUniversities.pending, (state) => { state.loading = true; })
      .addCase(fetchUniversities.fulfilled, (state, action) => {
        state.loading = false;
        state.universities = action.payload.data || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUniversities.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      // Fetch single university
      .addCase(fetchUniversityById.pending, (state) => { state.loading = true; })
      .addCase(fetchUniversityById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUniversity = action.payload;
      })
      .addCase(fetchUniversityById.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      // Create university
      .addCase(createUniversity.fulfilled, (state, action) => { 
        state.universities.unshift(action.payload); 
      })
      // Update university
      .addCase(updateUniversity.fulfilled, (state, action) => {
        const index = state.universities.findIndex(u => u.id === action.payload.id);
        if (index !== -1) state.universities[index] = action.payload;
        if (state.selectedUniversity?.id === action.payload.id) {
          state.selectedUniversity = action.payload;
        }
      })
      // Delete university
      .addCase(deleteUniversity.fulfilled, (state, action) => {
        state.universities = state.universities.filter(u => u.id !== action.payload);
        if (state.selectedUniversity?.id === action.payload) {
          state.selectedUniversity = null;
        }
      });
  },
});

export const { clearError, clearSelectedUniversity } = universitySlice.actions;
export default universitySlice.reducer;
